/**
 * 投放时间组合编辑器（Apple Ads 红线规则 · field=投放时间 专用）。
 *
 * 数据契约：ope_value =
 *   {"startHMs":"09:00","endHMs":"24:00","exemptKeywords":["关键词A","关键词B"]}
 * （三键：投放时段起止 + 豁免关键词列表；豁免关键词为命中投放时段红线时跳过
 *   告警的关键词，区别于华为的豁免任务ID exemptTaskIds）。
 *
 * 组合实现（merge 语义，与华为 LaunchTimeRangeEditor 同构）：HalfHourBlocksPicker
 * 的序列化契约只产出 startHMs/endHMs 两键（本组件不改其契约），故两条交互路径
 * 分别 merge 后全量 emit：
 *   - 拖拽色块：取 picker 新产出的 startHMs/endHMs + 当前 value 的既有 exemptKeywords；
 *   - 增删豁免关键词：取当前 value 的 startHMs/endHMs（原样保留、不重排不格式化）
 *     + 新关键词。
 * emit 为唯一序列化出口，键序固定 startHMs → endHMs → exemptKeywords；豁免列表
 * 清空时恒输出 "exemptKeywords":[] 而非省略键（与后端校验/展示契约一致，避免
 * 「省略键」被误读为未配置）。
 *
 * 维护警示：未来 ope_value 增第四键时，须同步本组件 parse/emit 的键集合，
 * 否则任何一次交互都会静默丢弃该键。
 */

import { useMemo } from "react";
import { HalfHourBlocksPicker } from "../huaweiRedlineConfig/HalfHourBlocksPicker";
import { IdChips } from "../redlineConfig/IdChips";

/** 三键解析结果（startHMs/endHMs 为字符串则原样保留 30 分钟对齐值，不格式化）。 */
interface AppleLaunchTimeValue {
  startHMs: string;
  endHMs: string;
  exemptKeywords: string[];
}

/** 编辑器 props：value 为 ope_value JSON 串，onChange 回传序列化后的新串。 */
interface AppleLaunchTimeRangeEditorProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** ope_value JSON → 三键；非法 JSON/非对象降级为未选择空值（不报错）。 */
function parseValue(value: string): AppleLaunchTimeValue {
  try {
    const obj: unknown = JSON.parse(value);
    if (obj !== null && typeof obj === "object") {
      const rec = obj as Record<string, unknown>;
      // exemptKeywords 仅当为数组且逐元素为 string 才采纳（元素级过滤，防脏数据）
      const keywords = Array.isArray(rec.exemptKeywords)
        ? rec.exemptKeywords.filter((x): x is string => typeof x === "string")
        : [];
      return {
        startHMs: typeof rec.startHMs === "string" ? rec.startHMs : "00:00",
        endHMs: typeof rec.endHMs === "string" ? rec.endHMs : "00:00",
        exemptKeywords: keywords,
      };
    }
  } catch {
    // 非法 JSON：降级为未选择 + 空豁免关键词，由用户重新录入
  }
  return { startHMs: "00:00", endHMs: "00:00", exemptKeywords: [] };
}

export function AppleLaunchTimeRangeEditor({
  value,
  onChange,
  disabled = false,
}: AppleLaunchTimeRangeEditorProps) {
  const parsed = useMemo(() => parseValue(value), [value]);

  /** 色块拖拽：picker 新值只含两键，须 merge 既有 exemptKeywords 再全量 emit。 */
  function onPickerChange(pickerNext: string) {
    const next = parseValue(pickerNext);
    onChange(
      JSON.stringify({
        startHMs: next.startHMs,
        endHMs: next.endHMs,
        exemptKeywords: parsed.exemptKeywords,
      }),
    );
  }

  /** 增删豁免关键词：时段两键原样保留（不重排不格式化），merge 新关键词全量 emit。 */
  function onKeywordsChange(keywords: string[]) {
    onChange(
      JSON.stringify({
        startHMs: parsed.startHMs,
        endHMs: parsed.endHMs,
        exemptKeywords: keywords,
      }),
    );
  }

  return (
    <div className="w-full">
      <HalfHourBlocksPicker value={value} onChange={onPickerChange} disabled={disabled} />
      {/* 豁免关键词：label 传入 IdChips 走两行布局——label 与 chips 同行对齐，
          输入框 + 「添加」在 label 正下方一行（避免 chips 多时输入框挤到行尾） */}
      <div className="mt-2.5">
        <IdChips
          ids={parsed.exemptKeywords}
          onChange={onKeywordsChange}
          disabled={disabled}
          label="豁免关键词"
          placeholder="输入关键词，回车添加"
        />
      </div>
      <div className="mt-1 text-[11px] text-text-muted-weak">
        命中投放时段红线时跳过告警的关键词
      </div>
    </div>
  );
}
